<template>
  <dialog
    ref="modalRef"
    class="modal absolute top-0 left-0 m-0 p-0 border-none rounded-lg min-w-[18vw] max-w-[90vw] overflow-hidden shadow-[0_2px_8px_rgba(0,0,0,0.25)]"
    @close="handleDialogClose"
  >
    <div class="flex flex-col overflow-hidden rounded-lg">
      <div
        class="flex justify-between items-center p-4 cursor-move bg-muted border-b border-border"
        @mousedown="startDrag"
      >
        <h2 class="m-0 text-xl select-none">{{ title }}</h2>
        <BaseIconButton icon="times" type="default" @click="handleDialogClose" />
      </div>

      <div class="flex flex-col py-2 px-4 overflow-y-auto flex-1 max-h-[calc(90vh-72px-3rem)]">
        <slot />
      </div>

      <div class="py-2 px-4 flex justify-end gap-2 border-t border-border">
        <slot name="footer">
          <BaseButton icon="times" type="default" @click="handleDialogClose">
            Lukk
          </BaseButton>
        </slot>
      </div>
    </div>
  </dialog>
</template>

<script setup>
import { ref, watch, onMounted, onBeforeUnmount } from 'vue'
import BaseIconButton from '@/components/BaseComponents/BaseIconButton.vue'
import BaseButton from '@/components/BaseComponents/BaseButton.vue'

const props = defineProps({
  modelValue: {
    type: Boolean,
    required: true
  },
  title: {
    type: String,
    required: true
  },
  fullHeight: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:modelValue', 'close'])
const modalRef = ref(null)

// Settes true første gang brukeren drar dialogen. Mens den er true gjøres ingen
// auto-re-sentrering (vi respekterer brukerens valgte posisjon). Nullstilles når
// dialogen lukkes, så neste åpning sentreres på nytt.
const userHasPositioned = ref(false)

// ResizeObserver er imperativt DOM-API (tillatt unntak fra watch-regelen, på
// linje med <dialog>-watch under). Re-sentrerer når innholdshøyden vokser
// (f.eks. validerings-feil) før dialogen rakk å bli målt ved åpning.
let resizeObserver = null;

function handleDialogClose () {
  if (props.modelValue) {
    emit('update:modelValue', false)
    emit('close')
  }
}

function centerDialog () {
  const rect = modalRef.value.getBoundingClientRect();
  const left = Math.max(0, (window.innerWidth - rect.width) / 2);
  const top = Math.max(0, (window.innerHeight - rect.height) / 2);

  modalRef.value.style.left = `${left}px`;
  modalRef.value.style.top = `${top}px`;
}

function connectResizeObserver () {
  if (resizeObserver || !modalRef.value) {
    return;
  }

  resizeObserver = new ResizeObserver(() => {
    if (props.modelValue && !userHasPositioned.value) {
      centerDialog();
    }
  });
  resizeObserver.observe(modalRef.value);
}

function disconnectResizeObserver () {
  if (resizeObserver) {
    resizeObserver.disconnect();
    resizeObserver = null;
  }
}

function openDialog () {
  modalRef.value.showModal();
  centerDialog();
  connectResizeObserver();
}

function closeDialog () {
  modalRef.value.close();
  disconnectResizeObserver();
  userHasPositioned.value = false;
}

onMounted(() => {
  if (props.modelValue && modalRef.value) {
    openDialog();
  }
});

// SANKSJONERT watch (imperativ DOM-API). Native <dialog> styres KUN via showModal()/close(),
// og showModal() krever at elementet er koblet til DOM-en. Derfor MÅ dialoger være ALLTID
// MONTERT — aldri `v-if` i parent. Synlighet = modelValue → showModal/close (her).
// Konsumenter: ikke seed skjema via watch(modelValue) eller uren computed — la draften bo i en
// Pinia-store og seed den i åpne-actionen (store.startEdit/startNew) før modelValue settes true.
// Se .github/copilot-instructions.md §Vue 3 «<dialog>-komponenter MÅ være ALLTID MONTERT».
watch(() => props.modelValue, (newValue) => {
  if (newValue && modalRef.value) {
    openDialog();
  } else if (modalRef.value) {
    closeDialog();
  }
});

onBeforeUnmount(() => {
  disconnectResizeObserver();
});

let startX, startY, initialLeft, initialTop, initialWidth, initialHeight;

function startDrag (event) {
  userHasPositioned.value = true;
  startX = event.clientX;
  startY = event.clientY;

  const rect = modalRef.value.getBoundingClientRect();
  initialLeft = rect.left;
  initialTop = rect.top;
  initialWidth = rect.width;
  initialHeight = rect.height;

  document.addEventListener('mousemove', drag);
  document.addEventListener('mouseup', stopDrag);
}

function drag (event) {
  const deltaX = event.clientX - startX;
  const deltaY = event.clientY - startY;

  let newLeft = initialLeft + deltaX;
  let newTop = initialTop + deltaY;

  // Beregn maksimale grenser
  const maxLeft = window.innerWidth - initialWidth;
  const maxTop = window.innerHeight - initialHeight;

  // Begrens dialogens posisjon til viewport
  newLeft = Math.max(0, Math.min(newLeft, maxLeft));
  newTop = Math.max(0, Math.min(newTop, maxTop));

  modalRef.value.style.left = `${newLeft}px`;
  modalRef.value.style.top = `${newTop}px`;
}

function stopDrag () {
  document.removeEventListener('mousemove', drag);
  document.removeEventListener('mouseup', stopDrag);
}
</script>

<style scoped>
/* ::backdrop er en pseudo-element på <dialog> som Tailwind utilities ikke
   kan adressere — beholdes lokalt. */
.modal::backdrop {
  background: rgba(0, 0, 0, 0.5);
}
</style>
